import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, Loader2, AlertCircle, Settings } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { TimesheetDeadlineSettings } from '@/components/TimesheetDeadlineSettings';
import { UtilizationSettings } from '@/components/UtilizationSettings';
import { WorkingHoursConfigurationCompact } from '@/components/settings/WorkingHoursConfigurationCompact';
import type { 
  TimesheetDeadlineSettings as DeadlineSettingsType,
  UtilizationSettings as UtilizationSettingsType,
  OrganizationTimesheetSettings,
  WorkingProfile,
  PolicyViolationSettings,
  PolicyLimits
} from '@/types/timesheetSettings';
import { toast } from 'sonner';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { ApiError } from '@/services/apiClient';
import * as settingsRepository from '@/services/settingsRepository';
import { workingPatternToProfile } from '@/services/settingsRepository';

interface SettingsPageProps {
  onNavigate?: (path: string) => void;
}

export const SettingsPage: React.FC<SettingsPageProps> = ({ onNavigate }) => {
  const { mode, isDemo, isLive } = useScreenDataMode('settings-general');
  
  const [deadlineSettings, setDeadlineSettings] = useState<DeadlineSettingsType | null>(null);
  const [utilizationSettings, setUtilizationSettings] = useState<UtilizationSettingsType | null>(null);
  const [workingProfile, setWorkingProfile] = useState<WorkingProfile | null>(null);
  const [policyEnforcement, setPolicyEnforcement] = useState<PolicyViolationSettings | null>(null);
  const [policyLimits, setPolicyLimits] = useState<PolicyLimits | undefined>(undefined);
  const [fullSettings, setFullSettings] = useState<OrganizationTimesheetSettings | null>(null);
  const [rowVersion, setRowVersion] = useState<string | undefined>(undefined);
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleDeadlineChange = useCallback((settings: DeadlineSettingsType, isValid: boolean) => {
    setDeadlineSettings(settings);
  }, []);

  const handleUtilizationChange = useCallback((settings: UtilizationSettingsType, isValid: boolean) => {
    setUtilizationSettings(settings);
  }, []);

  const handleWorkingProfileChange = useCallback((profile: WorkingProfile) => {
    setWorkingProfile(profile);
  }, []);

  const handlePolicyEnforcementChange = useCallback((enforcement: PolicyViolationSettings) => {
    setPolicyEnforcement(enforcement);
  }, []);

  const handlePolicyLimitsChange = useCallback((limits: PolicyLimits | undefined) => {
    setPolicyLimits(limits);
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch both timesheet settings and working pattern in parallel
      const [settings, workingPattern] = await Promise.all([
        settingsRepository.getOrganizationTimesheetSettings(mode),
        settingsRepository.getOrganizationWorkingPattern(mode),
      ]);
      
      setFullSettings(settings);
      setDeadlineSettings(settings.deadline);
      setUtilizationSettings(settings.utilization);
      
      // Convert WorkingPattern from API to WorkingProfile format for the UI
      // This ensures we read from the same source (OrganizationWorkingPattern) that we write to
      const loadedWorkingProfile = workingPatternToProfile(workingPattern);
      setWorkingProfile(loadedWorkingProfile);
      
      setPolicyEnforcement(settings.policyViolations || {
        suppressDailyWhenWeeklyExceeded: true,
        consolidateDailyWarnings: true,
        checks: {
          'daily-overage': { enabled: true, threshold: 1.25, severity: 'warning' },
          'weekly-overage': { enabled: true, threshold: 1.10, severity: 'warning' },
          'legal-daily-maximum': { enabled: false, severity: 'critical' },
          'legal-weekly-maximum': { enabled: false, severity: 'critical' },
          'missing-hours': { enabled: true, threshold: 0.80, severity: 'info' },
          'non-working-day': { enabled: true, severity: 'info' }
        },
        displayMode: 'smart',
        showInfoAlerts: true,
        preset: 'balanced'
      });
      setPolicyLimits(settings.policyLimits);
      
      if ('rowVersion' in settings) {
        setRowVersion(settings.rowVersion);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'Failed to load settings. Please try again.';
      setError(errorMessage);
      
      if (isLive) {
        toast.error('Failed to load settings', {
          description: 'Could not connect to API. Please ensure the backend is running or switch to demo mode.',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [mode]);

  const handleSaveDeadline = async (settings: DeadlineSettingsType) => {
    setIsSaving(true);
    
    try {
      const updatedSettings = await settingsRepository.updateDeadlineSettings(
        settings,
        fullSettings || undefined,
        rowVersion,
        mode
      );
      
      setDeadlineSettings(settings);
      setFullSettings(updatedSettings);
      
      if ('rowVersion' in updatedSettings) {
        setRowVersion(updatedSettings.rowVersion);
      }
      
      toast.success('Timesheet deadline settings saved', {
        description: 'Changes will apply to all timesheets',
      });
    } catch (err) {
      console.error('Error saving deadline settings:', err);
      
      if (err instanceof ApiError && err.status === 409) {
        toast.error('Settings conflict', {
          description: 'Settings were modified by another user. Reloading...',
        });
        await loadData();
      } else {
        toast.error('Failed to save deadline settings', {
          description: err instanceof ApiError ? err.message : 'Please try again',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveUtilization = async (settings: UtilizationSettingsType) => {
    setIsSaving(true);
    
    try {
      const updatedSettings = await settingsRepository.updateUtilizationSettings(
        settings,
        fullSettings || undefined,
        rowVersion,
        mode
      );
      
      setUtilizationSettings(settings);
      setFullSettings(updatedSettings);
      
      if ('rowVersion' in updatedSettings) {
        setRowVersion(updatedSettings.rowVersion);
      }
      
      toast.success('Utilization settings saved', {
        description: 'Changes will affect dashboard metrics and reports',
      });
    } catch (err) {
      console.error('Error saving utilization settings:', err);
      
      if (err instanceof ApiError && err.status === 409) {
        toast.error('Settings conflict', {
          description: 'Settings were modified by another user. Reloading...',
        });
        await loadData();
      } else {
        toast.error('Failed to save utilization settings', {
          description: err instanceof ApiError ? err.message : 'Please try again',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveWorkingHoursSettings = async () => {
    if (!workingProfile || !policyEnforcement) return;
    
    setIsSaving(true);
    
    try {
      const updatedSettings = await settingsRepository.updateWorkingHoursSettings(
        workingProfile,
        policyEnforcement,
        policyLimits,
        fullSettings || undefined,
        rowVersion,
        mode
      );
      
      setWorkingProfile(workingProfile);
      setPolicyEnforcement(policyEnforcement);
      setPolicyLimits(policyLimits);
      setFullSettings(updatedSettings);
      
      if ('rowVersion' in updatedSettings) {
        setRowVersion(updatedSettings.rowVersion);
      }
      
      toast.success('Working hours settings saved', {
        description: 'Changes will apply to all timesheet validation',
      });
    } catch (err) {
      console.error('Error saving working hours settings:', err);
      
      if (err instanceof ApiError && err.status === 409) {
        toast.error('Settings conflict', {
          description: 'Settings were modified by another user. Reloading...',
        });
        await loadData();
      } else {
        toast.error('Failed to save working hours settings', {
          description: err instanceof ApiError ? err.message : 'Please try again',
        });
      }
    } finally {
      setIsSaving(false);
    }
  };



  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">General Settings</h1>
          <p className="text-muted-foreground mt-2">
            Configure organization-wide defaults and preferences
          </p>
        </div>
        <div className="flex items-center justify-center p-12">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">General Settings</h1>
          <p className="text-muted-foreground mt-2">
            Configure organization-wide defaults and preferences
          </p>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <p className="font-medium mb-2">{error}</p>
            <Button onClick={loadData} variant="outline" size="sm">
              Try Again
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!deadlineSettings || !utilizationSettings || !workingProfile || !policyEnforcement) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">General Settings</h1>
          <p className="text-muted-foreground mt-2">
            Configure organization-wide defaults and preferences
          </p>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <p className="font-medium mb-2">Unable to load settings data</p>
            <p className="text-sm text-muted-foreground mb-3">
              Some required data is missing. This might be a data initialization issue.
            </p>
            <Button onClick={loadData} variant="outline" size="sm">
              Reload Settings
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const modeLabel = isLive
    ? 'Connected to API • Changes are saved to database'
    : isDemo
    ? 'Demo Mode • Changes are saved locally'
    : undefined;

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">General Settings</h1>
        <p className="text-muted-foreground mt-2">
          Configure organization-wide defaults and preferences
        </p>
        {modeLabel && (
          <p className="text-sm text-muted-foreground mt-1">{modeLabel}</p>
        )}
      </div>

      {/* Working Hours Configuration - Consolidated (replaces Organization Working Pattern, Working Profile, Policy Enforcement, and Policy Limits) */}
      <div className="space-y-6">
        <WorkingHoursConfigurationCompact
          workingProfile={workingProfile}
          policyEnforcement={policyEnforcement}
          policyLimits={policyLimits}
          onWorkingProfileChange={handleWorkingProfileChange}
          onPolicyEnforcementChange={handlePolicyEnforcementChange}
          onPolicyLimitsChange={handlePolicyLimitsChange}
        />
        
        <div className="flex justify-end">
          <Button 
            onClick={handleSaveWorkingHoursSettings}
            disabled={isSaving}
            size="lg"
            className="min-w-[200px]"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Save All Settings
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Timesheet Deadline Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Timesheet Deadline</CardTitle>
          <CardDescription>
            Configure when timesheets must be submitted
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <TimesheetDeadlineSettings
            settings={deadlineSettings}
            onChange={handleDeadlineChange}
          />
          
          <div className="flex justify-end">
            <Button 
              onClick={() => deadlineSettings && handleSaveDeadline(deadlineSettings)}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Save Deadline Settings
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Utilization Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Utilization & Performance</CardTitle>
          <CardDescription>
            Configure utilization tracking and performance metrics
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <UtilizationSettings
            settings={utilizationSettings}
            onChange={handleUtilizationChange}
          />
          
          <div className="flex justify-end">
            <Button 
              onClick={() => utilizationSettings && handleSaveUtilization(utilizationSettings)}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Save Utilization Settings
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Other Settings Placeholder */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Additional Settings</CardTitle>
          </div>
          <CardDescription>
            More configuration options coming soon
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-8 text-center text-muted-foreground">
            <p className="text-sm">
              For data source configuration, please visit{' '}
              <a href="/settings/screens" className="text-primary hover:underline font-medium">
                Screen Data Modes
              </a>
              .
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
